import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SparklesIcon, PlusIcon, SearchIcon, LayoutGridIcon, ListIcon, AlertCircleIcon, ArrowLeftIcon, EyeOffIcon, EyeIcon, FolderIcon, SlidersHorizontalIcon, MoreVerticalIcon, CheckIcon, HelpCircleIcon, TrendingUpIcon } from 'lucide-react';
import { useBusiness, CATALOG_LABEL } from './BusinessContext';
import { PremiumSelect } from './PremiumSelect';
import { CatalogItemModal } from './CatalogItemModal';
import { CatalogChat } from './CatalogChat';
import { UpgradePlanModal } from './UpgradePlanModal';
import { CatalogInstructionsModal } from './CatalogInstructionsModal';
import { AddItemFlow } from './catalog/AddItemFlow';
import { CategoryManager, MoveToCategory, UNCATEGORIZED } from './catalog/CategoryManager';
import { SellingRulesManager } from './catalog/SellingRulesManager';
import { catKey, seedRules, useSelling } from '../data/sellingRules';
import { CatalogItem, CATALOG_SETS } from '../data/catalogItems';
import { AVAILABILITY_COPY, DEFAULT_KIND, blueprint } from '../data/productCategories';
const STATUS_FILTERS = ['All', 'Active', 'Draft', 'Unavailable', 'Needs attention'];

/**
 * One catalogue for every trade. The layout is fixed; the content adapts to
 * whatever each item actually is, so a fleet, a fragrance shelf and a menu all
 * feel like the same Talab product.
 */
export function BusinessCatalog() {
  const {
    hub,
    canAddListing,
    currentPlanId,
    listingCount,
    publishListings,
    unpublishListings,
    PACKAGES
  } = useBusiness();
  const catalogName = CATALOG_LABEL[hub?.type ?? 'generic'] || 'Catalogue';
  const currentPlan = PACKAGES.find(p => p.id === currentPlanId) || PACKAGES[0];
  const limitLabel = currentPlan.listingLimit === Infinity ? 'Unlimited' : currentPlan.listingLimit;
  const [isPopulated, setIsPopulated] = useState(true);
  const [showChat, setShowChat] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [adding, setAdding] = useState(false);
  const [openItem, setOpenItem] = useState<CatalogItem | null>(null);
  const [setId, setSetId] = useState('rental');
  const [items, setItems] = useState<CatalogItem[]>(CATALOG_SETS[0].items);
  const [viewMode, setViewMode] = useState<'card' | 'table'>('card');
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('All');
  const [category, setCategory] = useState('All');
  const [talabFilter, setTalabFilter] = useState<'talab' | 'inventory'>('talab');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [managing, setManaging] = useState(false);
  const [moving, setMoving] = useState(false);
  /** Category-level selling recommendations, optionally focused on one. */
  const [selling, setSelling] = useState<{
    open: boolean;
    category?: string;
  } | null>(null);

  /* Categories are the merchant's own, seeded from their items — never a list
     of car body types for a perfume shop. Talab's internal AI classification
     is a separate thing the merchant never has to see or manage. */
  const [cats, setCats] = useState<string[]>(() => Array.from(new Set(CATALOG_SETS[0].items.map(i => i.category).filter(Boolean))));
  /** Anything without one of the merchant's categories reads as uncategorized. */
  const itemCategory = (i: CatalogItem) => i.category && cats.includes(i.category) ? i.category : UNCATEGORIZED;
  const allCats = useMemo(() => [...cats, UNCATEGORIZED], [cats]);
  const counts = useMemo(() => {
    const map: Record<string, number> = {};
    items.forEach(i => {
      const c = itemCategory(i);
      map[c] = (map[c] ?? 0) + 1;
    });
    return map;
  }, [items, cats]);

  /* Recommendations are set per category, so the trade of the catalogue — not
     of any single item — decides what Talab would propose. */
  const kind = items[0]?.kind ?? DEFAULT_KIND[hub?.type ?? 'generic'] ?? 'grocery';
  const {
    rules
  } = useSelling();
  useEffect(() => {
    seedRules(kind, cats);
  }, [kind, cats]);
  const ruleCount = (c: string) => {
    const r = rules[catKey(c)];
    if (!r) return 0;
    return r.better.length + r.addons.length;
  };
  const configuredCats = cats.filter(c => rules[catKey(c)]).length;
  const stats = useMemo(() => ({
    total: items.length,
    active: items.filter(i => i.status === 'Active' && i.talabPublished).length,
    attention: items.filter(i => i.missing.length > 0).length
  }), [items]);

  /** Everything in the current scope, before category and search narrow it —
      this is what the category counts are measured against. */
  const scoped = useMemo(() => items.filter(i => talabFilter === 'talab' ? i.talabPublished : true), [items, talabFilter]);
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter(i => {
      if (category !== 'All' && itemCategory(i) !== category) return false;
      if (status === 'Needs attention' && i.missing.length === 0) return false;
      if (status !== 'All' && status !== 'Needs attention' && i.status !== status) return false;
      // "All inventory" is everything you own, published or not.
      if (talabFilter === 'talab' && !i.talabPublished) return false;
      if (!q) return true;
      return i.title.toLowerCase().includes(q) || i.category.toLowerCase().includes(q) || i.details.some(d => d.value.toLowerCase().includes(q));
    });
  }, [items, query, status, category, talabFilter]);
  const selectedOff = items.filter(i => picked.has(i.id) && !i.talabPublished);
  const selectedOn = items.filter(i => picked.has(i.id) && i.talabPublished);
  const toggle = (id: string) => setPicked(s => {
    const n = new Set(s);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const publish = () => {
    const ids = selectedOff.map(i => i.id);
    if (!ids.length) return;
    if (listingCount + ids.length > currentPlan.listingLimit) {
      setShowUpgrade(true);
      return;
    }
    setItems(prev => prev.map(i => ids.includes(i.id) ? {
      ...i,
      talabPublished: true,
      status: 'Active'
    } : i));
    publishListings(ids.length);
    setPicked(new Set());
  };
  const unpublish = () => {
    const ids = selectedOn.map(i => i.id);
    if (!ids.length) return;
    setItems(prev => prev.map(i => ids.includes(i.id) ? {
      ...i,
      talabPublished: false
    } : i));
    unpublishListings(ids.length);
    setPicked(new Set());
  };

  /* --- categories. Deleting one never deletes the items in it. ---------- */

  const addCategory = (name: string) => setCats(c => [...c, name]);
  const renameCategory = (from: string, to: string) => {
    setCats(c => c.map(x => x === from ? to : x));
    setItems(prev => prev.map(i => i.category === from ? {
      ...i,
      category: to
    } : i));
    setCategory(c => c === from ? to : c);
  };
  const deleteCategory = (name: string) => {
    setCats(c => c.filter(x => x !== name));
    setCategory(c => c === name ? 'All' : c);
  };
  const moveSelected = (to: string) => {
    const ids = Array.from(picked);
    setItems(prev => prev.map(i => ids.includes(i.id) ? {
      ...i,
      category: to === UNCATEGORIZED ? '' : to
    } : i));
    setPicked(new Set());
    setMoving(false);
  };
  const switchSet = (id: string) => {
    const set = CATALOG_SETS.find(s => s.id === id)!;
    setSetId(id);
    setItems(set.items);
    setCats(Array.from(new Set(set.items.map(i => i.category).filter(Boolean))));
    setCategory('All');
    setStatus('All');
    setTalabFilter('talab');
    setPicked(new Set());
    // A merchant with nothing in their catalogue belongs in the AI build flow,
    // not in front of an empty grid.
    setIsPopulated(set.items.length > 0);
    setShowChat(false);
  };
  if (!isPopulated || showChat) return <div className="w-full max-w-6xl mx-auto h-full flex flex-col" data-id="element-346" data-mp="1x9tgu7h5ew4k">
        {isPopulated && showChat && <button onClick={() => setShowChat(false)} className="mb-4 inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-primary-indigo transition-colors duration-150 ease-out self-start" data-id="element-347" data-mp="14j2zba4nlhdu">
            <ArrowLeftIcon className="w-4 h-4" data-id="element-348" data-mp="1dlkn3szyii2y" />
            Back to {catalogName.toLowerCase()}
          </button>}
        <div className="flex-1 min-h-0 flex flex-col" data-id="element-349" data-mp="2kznsilpqeen8">
          <CatalogChat catalogName={catalogName} onPopulateDemo={() => {
        // Building from the empty state has to land on real items.
        if (items.length === 0) switchSet('rental');else {
          setIsPopulated(true);
          setShowChat(false);
        }
      }} data-id="element-350" data-mp="2oy4z9458pn44" />
        </div>
        {/* Prototype-only: the same catalogue, as a different kind of business */}
        <div className="flex items-center gap-2 flex-wrap justify-end pt-3 flex-shrink-0" data-id="element-351" data-mp="3qqpk3znrk995">
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-300" data-id="element-352" data-mp="3lrmh0krcgd3t">
            Preview as
          </span>
          {CATALOG_SETS.map(s => <button key={s.id} onClick={() => switchSet(s.id)} className={`px-2.5 py-1 rounded-lg text-[11px] font-bold transition-colors duration-150 ease-out ${setId === s.id ? 'bg-slate-100 text-dark-navy' : 'text-slate-400 hover:text-slate-600'}`} data-id="element-353" data-mp="3hqb0r5ud148a">
              {s.label}
            </button>)}
        </div>
      </div>;
  return <motion.div initial={{
    opacity: 0,
    y: 8
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.2,
    ease: 'easeOut'
  }} className="w-full max-w-6xl mx-auto space-y-6 pb-24" data-id="element-354" data-mp="1nt2i0d6it5vc">
      {/* The nav tab already says "Catalogue" — so this row is one line of
          meaning plus the two actions, and content starts higher. */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-id="element-355" data-mp="105b5b8h2iqtj">
        <div className="flex items-center gap-2.5 flex-wrap" data-id="element-356" data-mp="103hvxlf2lshu">
          <p className="text-sm text-slate-500" data-id="element-357" data-mp="1dxhgsh9a09mn">
            Everything you sell, rent or offer through Talab.
          </p>
          <span className="inline-flex items-center gap-2 px-2.5 py-1 bg-slate-100 rounded-full text-[11px] font-semibold text-slate-500" data-id="element-358" data-mp="193wjysjf5lp1">
            {listingCount} / {limitLabel} on Talab
          </span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0" data-id="element-359" data-mp="2s4p2eus6shkk">
          <button onClick={() => setShowInstructions(true)} className="px-3.5 py-2.5 text-sm font-semibold text-slate-500 rounded-xl hover:text-ai-purple transition-colors duration-150 ease-out inline-flex items-center gap-2" data-id="element-360" data-mp="2s3dxgrn093rv">
            <SparklesIcon className="w-4 h-4 text-ai-purple" data-id="element-361" data-mp="2o2al4f461pve" />
            Catalogue help
          </button>
          <button onClick={() => setManaging(true)} className="px-4 py-2.5 bg-white border border-slate-200 text-slate-700 text-sm font-semibold rounded-xl hover:border-primary-indigo/40 hover:text-primary-indigo transition-colors duration-150 ease-out inline-flex items-center gap-2" data-id="element-362" data-mp="3btprciz2crbx">
            <FolderIcon className="w-4 h-4" data-id="element-363" data-mp="36s0f5003u7m9" />
            <span className="hidden sm:inline" data-id="element-364" data-mp="1qfl7t940jqu8">Manage categories</span>
            <span className="sm:hidden" data-id="element-365" data-mp="1qhejj2thh193">Categories</span>
          </button>
          <button onClick={() => setSelling({
          open: true
        })} className="px-4 py-2.5 bg-white border border-slate-200 text-slate-700 text-sm font-semibold rounded-xl hover:border-ai-purple/40 hover:text-ai-purple transition-colors duration-150 ease-out inline-flex items-center gap-2" data-id="element-366" data-mp="1vfzk53cndmy2">
            <TrendingUpIcon className="w-4 h-4" data-id="element-367" data-mp="xju82ij86x81" />
            <span className="hidden lg:inline" data-id="element-368" data-mp="xj2l1w3p27td">Selling recommendations</span>
            <span className="lg:hidden" data-id="element-369" data-mp="sn3r7szljxlk">Recommendations</span>
            {configuredCats > 0 && <span className="px-1.5 py-0.5 rounded bg-ai-purple/10 text-[11px] font-bold text-ai-purple" data-id="element-370" data-mp="nleg6dcdjjxh">
                {configuredCats}
              </span>}
          </button>
          <button onClick={() => canAddListing ? setAdding(true) : setShowUpgrade(true)} className="px-5 py-2.5 bg-primary-indigo text-white text-sm font-bold rounded-xl hover:bg-indigo-700 transition-colors duration-150 ease-out inline-flex items-center gap-2" data-id="element-371" data-mp="enm19aakeeam">
            <PlusIcon className="w-4 h-4" data-id="element-372" data-mp="2gjbanml9wuw3" />
            Add item
          </button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-3 lg:gap-4" data-id="element-373" data-mp="2q9edsfm3zp85">
        <Kpi title="Total items" value={stats.total} onClick={() => {
        setStatus('All');
        setCategory('All');
      }} data-id="element-374" data-mp="2qapjikz4771w" />
        <Kpi title="Live on Talab" value={stats.active} onClick={() => setTalabFilter('talab')} data-id="element-375" data-mp="1xlqy9ezp3osd" />
        <Kpi title="Needs attention" value={stats.attention} alert={stats.attention > 0} onClick={() => setStatus('Needs attention')} data-id="element-376" data-mp="22lwgy5nzm0yb" />
      </div>

      {/* Where an item lives. The explanation is a tooltip, not a paragraph. */}
      <div className="flex items-center gap-2 flex-wrap" data-id="element-377" data-mp="3ixaa3j7rnf4e">
        {([{
        id: 'talab',
        label: 'Live on Talab'
      }, {
        id: 'inventory',
        label: 'All inventory'
      }] as const).map(seg => <button key={seg.id} onClick={() => setTalabFilter(seg.id)} className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors duration-150 ease-out ${talabFilter === seg.id ? 'bg-primary-indigo text-white border-primary-indigo' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`} data-id="element-378" data-mp="3glht6da9g5pa">
            {seg.label}
          </button>)}
        <span title="Inventory items can stay available in your private shop and website chat even if they are not published on Talab Discover." className="w-6 h-6 rounded-full flex items-center justify-center text-slate-300 hover:text-slate-500 cursor-help transition-colors duration-150 ease-out" data-id="element-379" data-mp="i1rl1m1djp3m">
          <HelpCircleIcon className="w-4 h-4" data-id="element-380" data-mp="n1vuis3v7jkc" />
        </span>
      </div>

      {/* Categories — the primary way to browse a large catalogue */}
      <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar -mx-1 px-1 pb-1" data-id="element-381" data-mp="15n1yf5q5kt4g">
        {[{
        name: 'All',
        count: scoped.length
      },
      // Uncategorized only appears when something is actually in it.
      ...allCats.map(c => ({
        name: c,
        count: scoped.filter(i => itemCategory(i) === c).length
      })).filter(c => c.name !== UNCATEGORIZED || c.count > 0)].map(c => <button key={c.name} onClick={() => setCategory(c.name)} className={`flex-shrink-0 inline-flex items-center gap-2 pl-3.5 pr-3 py-2 rounded-full text-[13px] font-semibold border transition-colors duration-150 ease-out ${category === c.name ? 'bg-dark-navy text-white border-dark-navy' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`} data-id="element-382" data-mp="dxyyg25au4xm">
            {c.name}
            <span className={`text-[11px] font-bold ${category === c.name ? 'text-white/60' : 'text-slate-400'}`} data-id="element-383" data-mp="2ap38m26qoxjr">
              {c.count}
            </span>
          </button>)}
        <button onClick={() => setManaging(true)} className="flex-shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[13px] font-bold text-primary-indigo border border-dashed border-primary-indigo/40 hover:bg-primary-indigo/[0.04] transition-colors duration-150 ease-out" data-id="element-384" data-mp="21wj78tz2cev8">
          <PlusIcon className="w-3.5 h-3.5" data-id="element-385" data-mp="2pj7snpna6pgl" />
          Category
        </button>
      </div>

      {/* Toolbar */}
      <div className="bg-white p-3 rounded-2xl border border-slate-200 flex flex-col md:flex-row md:items-center justify-between gap-3" data-id="element-386" data-mp="2ko29gyl9jow7">
        <div className="flex items-center gap-2 flex-wrap" data-id="element-387" data-mp="197c6rpg1tf7c">
          <div className="relative" data-id="element-388" data-mp="194psz82ke3go">
            <SearchIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-389" data-mp="147pt5ewb5wy0" />
            <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search inventory..." className="w-full md:w-64 pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:border-primary-indigo/50 focus:ring-4 focus:ring-primary-indigo/10 transition-[border-color,box-shadow] duration-150 ease-out" data-id="element-390" data-mp="u53r7fvon7os" />
          </div>
          <PremiumSelect compact icon={SlidersHorizontalIcon} value={status} options={STATUS_FILTERS} onChange={setStatus} className="min-w-[10rem]" data-id="element-391" data-mp="1cuaap54aw3bn" />
        </div>
        <div className="flex items-center gap-3 self-start" data-id="element-392" data-mp="1rschdo6mp4x4">
          <span className="text-[12px] font-semibold text-slate-400 whitespace-nowrap" data-id="element-393" data-mp="1rpq9fnftsned">
            {visible.length} of {scoped.length}
          </span>
          <div className="flex bg-slate-100 p-1 rounded-xl" data-id="element-394" data-mp="3tk7g737sdexx">
          {([['card', LayoutGridIcon, 'Cards'], ['table', ListIcon, 'List']] as const).map(([id, Icon, label]) => <button key={id} onClick={() => setViewMode(id)} className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors duration-150 ease-out inline-flex items-center gap-2 ${viewMode === id ? 'bg-white shadow-sm text-primary-indigo' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-395" data-mp="1ua4q9n0kbj64">
              <Icon className="w-4 h-4" data-id="element-396" data-mp="1be68o054h3dc" />
              <span className="hidden sm:inline" data-id="element-397" data-mp="1bfhgcb1ouv9q">{label}</span>
            </button>)}
          </div>
        </div>
      </div>

      {visible.length === 0 ? <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center" data-id="element-398" data-mp="1larjj23tp8p3">
          <h3 className="font-display font-bold text-lg text-dark-navy" data-id="element-399" data-mp="30r9usnyflojb">
            {category !== 'All' && !query && status === 'All' ? `No items in ${category} yet` : 'Nothing matches that'}
          </h3>
          <p className="text-sm text-slate-500 mt-1.5" data-id="element-400" data-mp="34cbm9j0qmfvl">
            {category !== 'All' && !query && status === 'All' ? 'Add something new, or move items you already have into this category.' : 'Try a different filter, or add your next item.'}
          </p>
          <div className="flex items-center justify-center gap-2 mt-5" data-id="element-401" data-mp="2zbntoswv1qz9">
            <button onClick={() => canAddListing ? setAdding(true) : setShowUpgrade(true)} className="px-4 py-2.5 bg-primary-indigo text-white text-sm font-bold rounded-xl hover:bg-indigo-700 transition-colors duration-150 ease-out inline-flex items-center gap-2" data-id="element-402" data-mp="3n1rms6cfa8c0">
              <PlusIcon className="w-4 h-4" data-id="element-403" data-mp="3i7x6lcw10bho" />
              Add item
            </button>
            {category !== 'All' && <button onClick={() => setCategory('All')} className="px-4 py-2.5 bg-white border border-slate-200 text-sm font-semibold text-slate-700 rounded-xl hover:border-slate-300 transition-colors duration-150 ease-out" data-id="element-404" data-mp="ekn39twgzt4w">
                Move existing items
              </button>}
          </div>
        </div> : viewMode === 'card' ? <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-405" data-mp="21f12pi07ktp2">
          {visible.map(item => <ItemCard key={item.id} item={item} picked={picked.has(item.id)} onPick={() => toggle(item.id)} onOpen={() => setOpenItem(item)} data-id="element-406" data-mp="1nf9ujxwdi7iz" />)}
        </div> : <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden overflow-x-auto" data-id="element-407" data-mp="1djoljfsw06zq">
          <table className="w-full min-w-[760px]" data-id="element-408" data-mp="37vd5xxhw11ri">
            <thead data-id="element-409" data-mp="37wo9pxbc29im">
              <tr className="border-b border-slate-200" data-id="element-410" data-mp="37w68em9td53u">
                {['', 'Item', 'Category', 'Price', 'Availability', 'Talab status', 'Needs attention', ''].map((h, i) => <th key={i} className="text-left text-[11px] font-bold uppercase tracking-wider text-slate-400 py-3 px-3 first:pl-5" data-id="element-411" data-mp="1axz2hkucsbiz" data-mpi={i}>
                      {h}
                    </th>)}
              </tr>
            </thead>
            <tbody data-id="element-412" data-mp="1ey9b759j5g1j">
              {visible.map(item => <ItemRow key={item.id} item={item} picked={picked.has(item.id)} onPick={() => toggle(item.id)} onOpen={() => setOpenItem(item)} data-id="element-413" data-mp="1tw1orrvinud3" />)}
            </tbody>
          </table>
        </div>}

      {/* Bulk actions */}
      <AnimatePresence data-id="element-414" data-mp="3qqekhx5c8jdt">
        {picked.size > 0 && <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 12
      }} transition={{
        duration: 0.18,
        ease: 'easeOut'
      }} className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3 px-4 py-3 bg-dark-navy text-white rounded-2xl shadow-large" data-id="element-415" data-mp="k7ushkwijx4c">
            <p className="text-sm font-semibold" data-id="element-416" data-mp="174wft1gaeskm">
              {picked.size} {picked.size === 1 ? 'item' : 'items'} selected
            </p>
            <button onClick={() => setMoving(true)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 text-xs font-bold hover:bg-white/20 transition-colors duration-150 ease-out" data-id="element-417" data-mp="1gxbxdxno9f4z">
              <FolderIcon className="w-3.5 h-3.5" data-id="element-418" data-mp="1qt5kgaz0or10" />
              Move to category
            </button>
            {selectedOff.length > 0 && <button onClick={publish} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 text-xs font-bold hover:bg-white/20 transition-colors duration-150 ease-out" data-id="element-419" data-mp="36dy1ox4wx247">
                <EyeIcon className="w-3.5 h-3.5" data-id="element-420" data-mp="2whusj7bn837r" />
                Publish on Talab
              </button>}
            {selectedOn.length > 0 && <button onClick={unpublish} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 text-xs font-bold hover:bg-white/20 transition-colors duration-150 ease-out" data-id="element-421" data-mp="3p5qjdvej70wm">
                <EyeOffIcon className="w-3.5 h-3.5" data-id="element-422" data-mp="3fct5k2khi04c" />
                Move to inventory
              </button>}
            <button onClick={() => setPicked(new Set())} className="text-xs font-bold text-white/60 hover:text-white transition-colors duration-150 ease-out" data-id="element-423" data-mp="byfedrzd5hbh">
              Clear
            </button>
          </motion.div>}
      </AnimatePresence>

      {/* Prototype-only: the same catalogue, as a different kind of business */}
      <div className="flex items-center gap-2 flex-wrap pt-2" data-id="element-424" data-mp="3ipqfdoti53f2">
        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-300" data-id="element-425" data-mp="3nn8fqqtgeabg">
          Preview as
        </span>
        {CATALOG_SETS.map(s => <button key={s.id} onClick={() => switchSet(s.id)} className={`px-2.5 py-1 rounded-lg text-[11px] font-bold transition-colors duration-150 ease-out ${setId === s.id ? 'bg-slate-100 text-dark-navy' : 'text-slate-400 hover:text-slate-600'}`} data-id="element-426" data-mp="k5gq3j2si2gf">
            {s.label}
          </button>)}
      </div>

      {managing && <CategoryManager categories={allCats} counts={counts} onClose={() => setManaging(false)} onAdd={addCategory} onRename={renameCategory} onDelete={deleteCategory} onReorder={next => setCats(next.filter(c => c !== UNCATEGORIZED))} recoCount={ruleCount} onOpenRecommendations={c => {
      setManaging(false);
      setSelling({
        open: true,
        category: c
      });
    }} data-id="element-427" data-mp="12w7zgkms1sut" />}
      {moving && <MoveToCategory count={picked.size} categories={cats} onClose={() => setMoving(false)} onMove={moveSelected} onCreate={addCategory} data-id="element-428" data-mp="24oasqwtl2oy9" />}

      {openItem && <CatalogItemModal item={openItem} categories={cats} onOpenSellingRules={c => setSelling({
      open: true,
      category: c
    })} onClose={() => setOpenItem(null)} data-id="element-429" data-mp="2dm4ejdtq2076" />}
      {selling?.open && <SellingRulesManager kind={kind} categories={cats} counts={counts} initialCategory={selling.category} onClose={() => setSelling(null)} data-id="element-430" data-mp="2hr3ke135l5ak" />}
      {adding && <AddItemFlow defaultKind={DEFAULT_KIND[hub?.type ?? 'generic'] ?? 'grocery'} categories={cats} onCreateCategory={addCategory} onClose={() => setAdding(false)} onAdd={item => {
      setItems(prev => [item, ...prev]);
      setTalabFilter('inventory');
      setAdding(false);
    }} data-id="element-431" data-mp="2rqcdysu3whwe" />}
      {showUpgrade && <UpgradePlanModal onClose={() => setShowUpgrade(false)} data-id="element-432" data-mp="28090h3ddqhsa" />}
      {showInstructions && <CatalogInstructionsModal onClose={() => setShowInstructions(false)} data-id="element-433" data-mp="2csa6i7jyfc9v" />}
    </motion.div>;
}

/* ------------------------------------------------------------------ pieces */

function Kpi({
  title,
  value,
  alert,
  onClick
}: {
  title: string;
  value: number;
  alert?: boolean;
  onClick?: () => void;
}) {
  return <button onClick={onClick} disabled={!onClick} className={`text-left bg-white rounded-2xl px-5 py-4 border transition-colors duration-150 ease-out ${alert ? 'border-warning/40 hover:border-warning' : 'border-slate-200 hover:border-slate-300'} ${onClick ? 'cursor-pointer' : 'cursor-default'}`} data-id="element-434" data-mp="1crf5ejhc57q9">
      <p className="text-[13px] font-medium text-slate-500" data-id="element-435" data-mp="yus09tf8bb99">{title}</p>
      <div className="flex items-center gap-2 mt-1.5" data-id="element-436" data-mp="tunepozgrqc6">
        <p className={`font-display font-bold text-2xl leading-none tracking-tight ${alert ? 'text-warning' : 'text-dark-navy'}`} data-id="element-437" data-mp="p2414s0s0v7d">
          {value}
        </p>
        {alert && <AlertCircleIcon className="w-4 h-4 text-warning" data-id="element-438" data-mp="g27gd1hicghl" />}
      </div>
    </button>;
}

/** How available something is, said the way that trade says it. */
function availabilityLabel(item: CatalogItem): string {
  const model = blueprint(item.kind).availability;
  const copy = AVAILABILITY_COPY[model];
  if (item.status === 'Unavailable') return copy.none;
  if (!copy.counts) return copy.plenty(0);
  const n = item.stock ?? 0;
  if (n === 0) return copy.none;
  return n <= 3 ? copy.low(n) : copy.plenty(n);
}
function StatusBadge({
  status
}: {
  status: string;
}) {
  const tone: Record<string, string> = {
    Active: 'bg-success/10 text-success',
    Draft: 'bg-slate-100 text-slate-500',
    Unavailable: 'bg-warning/10 text-warning',
    Archived: 'bg-slate-100 text-slate-400'
  };
  return <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider ${tone[status] ?? tone.Draft}`} data-id="element-439" data-mp="1g0etrdgaz7dx">
      {status}
    </span>;
}
function ItemCard({
  item,
  picked,
  onPick,
  onOpen
}: {
  item: CatalogItem;
  picked: boolean;
  onPick: () => void;
  onOpen: () => void;
}) {
  const bp = blueprint(item.kind);
  return <div className={`bg-white rounded-2xl border overflow-hidden transition-colors duration-150 ease-out ${picked ? 'border-primary-indigo' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-440" data-mp="sd6xtm4rlaok">
      <div className="relative" data-id="element-441" data-mp="dqhoytomhl5i">
        <button onClick={onOpen} className="block w-full h-40 bg-slate-100 overflow-hidden" aria-label={item.title} data-id="element-442" data-mp="8pkb2tanbd9t">
          {item.image ? <img src={item.image} alt="" className="w-full h-full object-cover" data-id="element-443" data-mp="inhwl914evyq" /> : <span className="w-full h-full flex items-center justify-center text-xs font-semibold text-slate-300" data-id="element-444" data-mp="wlyb3m585qrf">
              No photo yet
            </span>}
        </button>
        <button onClick={onPick} aria-label={`Select ${item.title}`} className={`absolute top-3 left-3 w-[18px] h-[18px] rounded-md border flex items-center justify-center transition-colors duration-150 ease-out ${picked ? 'bg-primary-indigo border-primary-indigo' : 'bg-white/90 border-white/90 hover:border-slate-300'}`} data-id="element-445" data-mp="16c143mccj4pr">
          {picked && <CheckIcon className="w-3 h-3 text-white" data-id="element-446" data-mp="2crdu09ijrd5p" />}
        </button>
        <span className="absolute top-3 right-3" data-id="element-447" data-mp="2cohtcdfbeadk">
          <StatusBadge status={item.status} data-id="element-448" data-mp="27sssm6rk6nfo" />
        </span>
      </div>

      <div className="p-4" data-id="element-449" data-mp="22selcqxs7hip">
        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400" data-id="element-450" data-mp="22rmu1sb7w5y8">
          {item.category || UNCATEGORIZED} · {bp.label}
        </p>
        <button onClick={onOpen} className="block text-left font-bold text-dark-navy mt-1.5 hover:text-primary-indigo transition-colors duration-150 ease-out truncate w-full" data-id="element-451" data-mp="1trzw5ghp5i9x">
          {item.title}
        </button>
        <div className="flex items-center justify-between gap-3 mt-2" data-id="element-452" data-mp="1k2yg5qb5j0vy">
          <p className="font-display font-bold text-lg text-dark-navy" data-id="element-453" data-mp="1f3lu37s3txuf">
            {item.priceLabel}
          </p>
          <p className="text-xs font-semibold text-slate-500" data-id="element-454" data-mp="1b0gsr8jrg27d">
            {availabilityLabel(item)}
          </p>
        </div>

        {item.missing.length > 0 ?
      // Naming the fix, and going straight to it.
      <button onClick={onOpen} className="inline-flex items-center gap-1.5 mt-3 text-[11px] font-bold text-warning hover:text-amber-700 transition-colors duration-150 ease-out" data-id="element-455" data-mp="3hp90056a4eeu">
            <AlertCircleIcon className="w-3.5 h-3.5" data-id="element-456" data-mp="3qocl1ep0b3vz" />
            {item.missing[0]}
            {item.missing.length > 1 ? ` +${item.missing.length - 1}` : ''}
          </button> : <p className="mt-3 text-[11px] font-semibold text-slate-400" data-id="element-457" data-mp="2z1423ecaznny">
            AI ready {item.aiScore}%
          </p>}
      </div>
    </div>;
}
function ItemRow({
  item,
  picked,
  onPick,
  onOpen
}: {
  item: CatalogItem;
  picked: boolean;
  onPick: () => void;
  onOpen: () => void;
}) {
  return <tr className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60 transition-colors duration-150 ease-out" data-id="element-458" data-mp="3csiolv5f66zr">
      <td className="pl-5 py-3" data-id="element-459" data-mp="2ka3h69ude1ax">
        <button onClick={onPick} aria-label={`Select ${item.title}`} className={`w-[18px] h-[18px] rounded-md border flex items-center justify-center transition-colors duration-150 ease-out ${picked ? 'bg-primary-indigo border-primary-indigo' : 'bg-white border-slate-300 hover:border-slate-400'}`} data-id="element-460" data-mp="2k926i6h9ngtq">
          {picked && <CheckIcon className="w-3 h-3 text-white" data-id="element-461" data-mp="3sbzc9ikdrjeo" />}
        </button>
      </td>
      <td className="py-3 px-3" data-id="element-462" data-mp="3se3pir465urm">
        <button onClick={onOpen} className="flex items-center gap-3 text-left" data-id="element-463" data-mp="34lwo3djl6xlc">
          <span className="w-10 h-10 rounded-xl bg-slate-100 overflow-hidden flex-shrink-0" data-id="element-464" data-mp="2znbptjnwanzj">
            {item.image && <img src={item.image} alt="" className="w-full h-full object-cover" data-id="element-465" data-mp="3dnd17t1sddt9" />}
          </span>
          <span className="min-w-0" data-id="element-466" data-mp="38mypo10z7lqh">
            <span className="block text-sm font-bold text-dark-navy truncate" data-id="element-467" data-mp="38q4i9q8td5qy">
              {item.title}
            </span>
          </span>
        </button>
      </td>
      <td className="py-3 px-3 text-sm text-slate-600 whitespace-nowrap" data-id="element-468" data-mp="nwzku0f89ss3">
        {item.category || <span className="text-slate-400" data-id="element-469" data-mp="we05qxhpysbn">{UNCATEGORIZED}</span>}
      </td>
      <td className="py-3 px-3 text-sm font-bold text-dark-navy whitespace-nowrap" data-id="element-470" data-mp="10hg2dttsgsoc">
        {item.priceLabel}
      </td>
      <td className="py-3 px-3 text-xs font-semibold text-slate-500 whitespace-nowrap" data-id="element-471" data-mp="mi6pb30vqeol">
        {availabilityLabel(item)}
      </td>
      <td className="py-3 px-3 whitespace-nowrap" data-id="element-472" data-mp="rhjis6m9ak05">
        {item.talabPublished ? <StatusBadge status={item.status} data-id="element-473" data-mp="1f7dv8n2ny6jv" /> : <span className="px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider bg-slate-100 text-slate-400" data-id="element-474" data-mp="1k3cig7c99iip">
            Off Talab
          </span>}
      </td>
      <td className="py-3 px-3 min-w-[150px]" data-id="element-475" data-mp="1adsuwodll0hi">
        {item.missing.length > 0 ? <button onClick={onOpen} className="inline-flex items-center gap-1.5 text-[11px] font-bold text-warning hover:text-amber-700 transition-colors duration-150 ease-out text-left" data-id="element-476" data-mp="1y3n5t0ecwez4">
            <AlertCircleIcon className="w-3.5 h-3.5 flex-shrink-0" data-id="element-477" data-mp="ea3zeyl54qxp" />
            {item.missing[0]}
            {item.missing.length > 1 ? ` +${item.missing.length - 1}` : ''}
          </button> : <span className="text-sm text-slate-300" data-id="element-478" data-mp="595qq2zrbv8s">—</span>}
      </td>
      <td className="py-3 px-3 text-right" data-id="element-479" data-mp="a2wzep89bg4">
        <button onClick={onOpen} aria-label={`Open ${item.title}`} className="p-2 rounded-lg text-slate-400 hover:text-primary-indigo hover:bg-white transition-colors duration-150 ease-out" data-id="element-480" data-mp="bdzlb6xsd5n">
          <MoreVerticalIcon className="w-4 h-4" data-id="element-481" data-mp="o6yt946gy35n" />
        </button>
      </td>
    </tr>;
}